import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from "src/environments/environment";
import { Observable, retry, Observer } from 'rxjs';
import { ApiRequestService } from './api-request.service';

type OptionsType = {
    headers?: HttpHeaders;
    params?: HttpParams;
    withCredentials?: boolean;
    reportProgress?: boolean;
}

type BufferResponseType = OptionsType & {
    responseType: "arraybuffer"
}

type RequestOptionType<IsBuffer extends boolean> = IsBuffer extends true ? BufferResponseType : OptionsType

const ClientError = "Something bad happened!!"


@Injectable({
  providedIn: 'root'
})
export class GenericApiService {
  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, private apiService: ApiRequestService) { }

  public get<T>(url: string, config: any = {}): Observable<{data:T | undefined, error: any}> {
    let httpOptions = this.getHttpOptions(config)
    let baseUrl = this.apiUrl
    if(config.baseUrl) baseUrl = config.baseUrl

    return new Observable((subscriber) => {
        this.http.get<T>(baseUrl+url, httpOptions)
        .pipe(retry(3))
        .subscribe({
            next : (response) =>{
                this.resolve<T>(subscriber, response, undefined);
            },
            error: (error) =>{
                this.resolve<T>(subscriber, undefined, error);
            },
            complete: () => console.info('complete') 
        })
    });
  }

  public post<T>(url: string, body: any, config: any = {}): Observable<{data:T | undefined, error: any}> {
    let httpOptions = this.getHttpOptions(config)
    let baseUrl = this.apiUrl
    if(config.baseUrl) baseUrl = config.baseUrl
    return new Observable((subscriber) => {
        this.http.post<T>(baseUrl+url, body, httpOptions)
        .pipe(retry(3))
        .subscribe({
            next : (response) =>{
                this.resolve<T>(subscriber, response, undefined);
            },
            error: (error) =>{
                this.resolve<T>(subscriber, undefined, error);
            },
            complete: () => console.info('complete') 
        })
    });
  }

  public put<T>(url: string, body: any, config: any = {}): Observable<{data:T | undefined, error: any}> {
    let httpOptions = this.getHttpOptions(config)
    let baseUrl = this.apiUrl
    if(config.baseUrl) baseUrl = config.baseUrl
    return new Observable((subscriber) => {
        this.http.put<T>(baseUrl+url,body, httpOptions)
        .pipe(retry(3))
        .subscribe({
            next : (response) =>{
                this.resolve<T>(subscriber, response, undefined);
            },
            error: (error) =>{
                this.resolve<T>(subscriber, undefined, error);
            },
            complete: () => console.info('complete') 
        })
    });
  }

  public delete<T>(url: string, config: any = {}): Observable<{data:T | undefined, error: any}> {
    let httpOptions = this.getHttpOptions(config)
    let baseUrl = this.apiUrl
    if(config.baseUrl) baseUrl = config.baseUrl
    return new Observable((subscriber) => {
        this.http.delete<T>(baseUrl+url, httpOptions)
        .pipe(retry(3))
        .subscribe({
            next : (response) =>{
                this.resolve<T>(subscriber, response, undefined);
            },
            error: (error) =>{
                this.resolve<T>(subscriber, undefined, error);
            },
            complete: () => console.info('complete') 
        })
    });
  }

  private getHttpOptions(config: any) {
    let headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8')
    let params = new HttpParams()
    if(config.headers) {
        Object.keys(config.headers).forEach(h=> {
            headers = headers.set(h, config.headers[h])
        })
    }

    if(config.query) {
        Object.keys(config.query).forEach(h=> {
            params = params.set(h, config.query[h])
        })
    }

    const isBuffer = config.isBuffer === true

    let options: RequestOptionType<typeof isBuffer> = {
        headers,
        params,
        withCredentials: config.hasOwnProperty("withCredentials") ? config.withCredentials : true,
        reportProgress: config.hasOwnProperty("reportProgress") ? config.reportProgress : false,
    }
   
    return options
  }

  private resolve<T> (subscriber: Observer<{data:T | undefined, error: any}>, response: T | undefined, error: any) {

    if(response !== undefined) {
        subscriber.next({data: response, error})
        subscriber.complete()
        return
    }
    
    this.handleError(error!, (e: any) => {
        subscriber.next({data: response, error: e})
        subscriber.complete()
    })
  }

  private handleError(error: HttpErrorResponse, cb: any) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
        cb({message: error.error.message})
    } else {
        this.apiService.handleError(error, cb)
    }
  };
}